/**
 * Zod schemas para validación runtime de datos de Firebase
 */
import { z } from 'zod';

// ========================================
// User & Auth Schemas
// ========================================

export const UserRoleSchema = z.enum([
  'super_admin',
  'franchise_owner',
  'admin',
  'barber',
  'client',
  'guest',
]);

export const CustomClaimsSchema = z.object({
  role: UserRoleSchema.optional(),
  isAnonymous: z.boolean().optional(),
  franchiseId: z.string().optional(),
  branchId: z.string().optional(),
});

export const QueueHistorySchema = z.object({
  totalCompleted: z.number().min(0),
  totalNoShows: z.number().min(0),
  totalExpired: z.number().min(0),
  totalCancelled: z.number().min(0),
});

export const UserSchema = z.object({
  id: z.string().min(1),
  email: z.string().email().nullable().optional().default(null), // Apple users may have no email or empty string
  displayName: z.string().min(1).optional().default('Usuario'), // Apple may not return name on subsequent logins
  photoURL: z.string().url().or(z.literal('')).nullable().optional(), // Optional - accepts URL, empty string, or null
  phoneNumber: z.string().nullable().optional(), // Optional - not all users have phone numbers
  role: UserRoleSchema.optional().default('client'), // May not be set yet if Cloud Function hasn't run
  isAnonymous: z.boolean().optional().default(false),
  franchiseId: z.string().optional(),
  branchId: z.string().optional(),
  queuePoints: z.number().optional().default(0),
  queueHistory: QueueHistorySchema.optional().default({
    totalCompleted: 0,
    totalNoShows: 0,
    totalExpired: 0,
    totalCancelled: 0,
  }),
  createdAt: z.any(), // Timestamp de Firestore
  updatedAt: z.any(), // Timestamp de Firestore
});

// ========================================
// Franchise & Branch Schemas
// ========================================

export const FranchiseSchema = z.object({
  franchiseId: z.string().min(1),
  name: z.string().min(1),
  ownerUserId: z.string().min(1),
  logo: z.string().url().optional(),
  description: z.string().optional(),
  website: z.string().url().optional(),
  email: z.string().email(),
  phone: z.string().min(1),
  stripeAccountId: z.string().optional(),
  stripeSubscriptionId: z.string().optional(),
  planTier: z.enum(['free', 'basic', 'premium', 'enterprise']),
  planExpiresAt: z.any().optional(), // Timestamp
  isActive: z.boolean(),
  createdAt: z.any(),
  updatedAt: z.any(),
});

export const DayScheduleSchema = z.object({
  isOpen: z.boolean(),
  open: z.string().optional(), // Can be empty string when closed
  close: z.string().optional(), // Can be empty string when closed
  breakStart: z.string().optional(),
  breakEnd: z.string().optional(),
}).refine(
  (data) => {
    // If open, must have valid times
    if (data.isOpen) {
      const timeRegex = /^\d{2}:\d{2}$/;
      return (
        data.open && timeRegex.test(data.open) &&
        data.close && timeRegex.test(data.close)
      );
    }
    return true;
  },
  {
    message: "Open and close times are required and must be in HH:MM format when the day is open",
  }
);

export const BranchScheduleSchema = z.object({
  monday: DayScheduleSchema,
  tuesday: DayScheduleSchema,
  wednesday: DayScheduleSchema,
  thursday: DayScheduleSchema,
  friday: DayScheduleSchema,
  saturday: DayScheduleSchema,
  sunday: DayScheduleSchema,
});

export const LocationSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

export const BranchSchema = z.object({
  branchId: z.string().min(1),
  franchiseId: z.string().min(1),
  name: z.string().min(1),
  address: z.string().min(1),
  city: z.string().min(1),
  postalCode: z.string().min(1),
  province: z.string().min(1),
  country: z.string().min(1),
  phone: z.string().min(1),
  email: z.string().email().optional(),
  photo: z.string().url().optional(),
  location: LocationSchema.optional(),
  schedule: BranchScheduleSchema,
  isActive: z.boolean(),
  createdAt: z.any(),
  updatedAt: z.any(),
});

// ========================================
// Barber Schemas
// ========================================

export const BarberDayScheduleSchema = z.object({
  isWorking: z.boolean(),
  workStart: z.string().optional(), // Can be empty string when not working
  workEnd: z.string().optional(), // Can be empty string when not working
  breakStart: z.string().optional(),
  breakEnd: z.string().optional(),
}).refine(
  (data) => {
    // If working, must have valid times
    if (data.isWorking) {
      const timeRegex = /^\d{2}:\d{2}$/;
      return (
        data.workStart && timeRegex.test(data.workStart) &&
        data.workEnd && timeRegex.test(data.workEnd)
      );
    }
    return true;
  },
  {
    message: "Work start and end times are required and must be in HH:MM format when the barber is working",
  }
);

export const BarberScheduleSchema = z.object({
  monday: BarberDayScheduleSchema,
  tuesday: BarberDayScheduleSchema,
  wednesday: BarberDayScheduleSchema,
  thursday: BarberDayScheduleSchema,
  friday: BarberDayScheduleSchema,
  saturday: BarberDayScheduleSchema,
  sunday: BarberDayScheduleSchema,
});

export const BarberSchema = z.object({
  barberId: z.string().min(1),
  userId: z.string().min(1),
  email: z.string().email().optional(), // Email del barbero
  franchiseId: z.string().min(1),
  branchId: z.string().min(1),
  displayName: z.string().min(1),
  photoURL: z.string().url().or(z.literal('')).nullable(), // Acepta URL válida, string vacío, o null
  specialties: z.array(z.string()),
  bio: z.string().max(500).optional(),
  schedule: BarberScheduleSchema.optional(), // Optional - some barbers may not have schedule yet
  isActive: z.boolean(),
  isAvailable: z.boolean().optional().default(true), // Optional with default true
  createdAt: z.any(),
  updatedAt: z.any(),
});

// ========================================
// Service Schemas
// ========================================

export const ServiceSchema = z.object({
  serviceId: z.string().min(1),
  franchiseId: z.string().min(1),
  branchId: z.string().optional(),
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  duration: z.number().min(5).max(480), // 5 min to 8 hours
  price: z.number().min(0), // cents
  currency: z.string().optional(),
  category: z.string().min(1),
  imageUrl: z.string().optional().refine((val) => !val || val === '' || z.string().url().safeParse(val).success, {
    message: 'Must be a valid URL or empty'
  }),
  isActive: z.boolean(),
  displayOrder: z.number().optional(),
  createdAt: z.any(),
  updatedAt: z.any(),
});

// ========================================
// Input Validation Schemas (for forms)
// ========================================

export const CreateFranchiseInputSchema = FranchiseSchema.omit({
  franchiseId: true,
  createdAt: true,
  updatedAt: true,
  stripeAccountId: true,
  stripeSubscriptionId: true,
}).extend({
  ownerUserId: z.string().min(1),
});

export const UpdateFranchiseInputSchema = CreateFranchiseInputSchema.partial();

export const CreateBranchInputSchema = BranchSchema.omit({
  branchId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  franchiseId: z.string().min(1),
});

export const UpdateBranchInputSchema = CreateBranchInputSchema.partial().omit({
  franchiseId: true,
});

export const CreateBarberInputSchema = BarberSchema.omit({
  barberId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  userId: z.string().min(1),
  franchiseId: z.string().min(1),
  branchId: z.string().min(1),
});

export const UpdateBarberInputSchema = CreateBarberInputSchema.partial().omit({
  userId: true,
  franchiseId: true,
  branchId: true,
});

export const CreateServiceInputSchema = ServiceSchema.omit({
  serviceId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  franchiseId: z.string().min(1),
});

export const UpdateServiceInputSchema = CreateServiceInputSchema.partial().omit({
  franchiseId: true,
});

// ========================================
// Validation Helper Functions
// ========================================

/**
 * Valida y parsea custom claims de forma segura
 */
export function parseCustomClaims(claims: unknown) {
  try {
    return CustomClaimsSchema.parse(claims);
  } catch (error) {
    console.error('Invalid custom claims:', error);
    return null;
  }
}

/**
 * Valida y parsea datos de usuario de Firestore
 */
export function parseUser(data: unknown) {
  try {
    // Sanitize common edge cases before validation (Apple Sign-In, etc.)
    if (data && typeof data === 'object') {
      const d = data as Record<string, unknown>;
      // Fix empty string email (Apple "Hide My Email" edge case)
      if (d.email === '' || d.email === undefined) {
        d.email = null;
      }
      // Fix missing/empty displayName
      if (!d.displayName || (typeof d.displayName === 'string' && d.displayName.trim() === '')) {
        d.displayName = 'Usuario';
      }
    }
    return UserSchema.parse(data);
  } catch (error) {
    // Log specific field errors for debugging
    if (error instanceof z.ZodError) {
      console.error('User validation failed:', error.issues.map(i => `${i.path.join('.')}: ${i.message}`));
    } else {
      console.error('Invalid user data:', error);
    }
    return null;
  }
}

/**
 * Valida y parsea datos de franquicia
 */
export function parseFranchise(data: unknown) {
  try {
    return FranchiseSchema.parse(data);
  } catch (error) {
    console.error('Invalid franchise data:', error);
    return null;
  }
}

/**
 * Valida y parsea datos de sucursal
 */
export function parseBranch(data: unknown) {
  try {
    return BranchSchema.parse(data);
  } catch (error) {
    console.error('Invalid branch data:', error);
    return null;
  }
}

/**
 * Valida y parsea datos de barbero
 */
export function parseBarber(data: unknown) {
  try {
    return BarberSchema.parse(data);
  } catch (error) {
    // Log specific field errors for debugging (same pattern as parseUser)
    if (error instanceof z.ZodError) {
      const id = (data as { id?: string; barberId?: string })?.id ?? (data as { barberId?: string })?.barberId ?? '?';
      console.error(`Barber validation failed (${id}):`, error.issues.map(i => `${i.path.join('.')}: ${i.message}`));
    } else {
      console.error('Invalid barber data:', error);
    }
    return null;
  }
}

/**
 * Valida y parsea datos de servicio
 */
export function parseService(data: unknown) {
  try {
    return ServiceSchema.parse(data);
  } catch (error) {
    console.error('Invalid service data:', error);
    return null;
  }
}

// ========================================
// Input Validation Functions (for forms)
// ========================================

export function validateCreateFranchise(data: unknown) {
  return CreateFranchiseInputSchema.safeParse(data);
}

export function validateUpdateFranchise(data: unknown) {
  return UpdateFranchiseInputSchema.safeParse(data);
}

export function validateCreateBranch(data: unknown) {
  return CreateBranchInputSchema.safeParse(data);
}

export function validateUpdateBranch(data: unknown) {
  return UpdateBranchInputSchema.safeParse(data);
}

export function validateCreateBarber(data: unknown) {
  return CreateBarberInputSchema.safeParse(data);
}

export function validateUpdateBarber(data: unknown) {
  return UpdateBarberInputSchema.safeParse(data);
}

export function validateCreateService(data: unknown) {
  return CreateServiceInputSchema.safeParse(data);
}

export function validateUpdateService(data: unknown) {
  return UpdateServiceInputSchema.safeParse(data);
}
